<template>
  <v-dialog :value="open" max-width="1400" persistent scrollable>
    <v-card>
      <v-card-title class="headline">
        {{ $t('producto.modals.cambioMasivoPrecio.title') }}
      </v-card-title>
      <v-card-text>
        <v-form ref="form" v-model="valid">
          <!-- Controles Globales -->
          <v-row>
            <v-col cols="12">
              <v-alert type="info" dense text class="mb-3">
                <v-icon left small>mdi-information</v-icon>
                {{ $t('producto.modals.cambioMasivoPrecio.hint') }}
              </v-alert>
            </v-col>
          </v-row>

          <v-row>
            <v-col cols="12" sm="6" md="2">
              <v-text-field v-model.number="precioCompraGlobal"
                :label="$t('producto.modals.cambioMasivoPrecio.precioCompraGlobal')" prepend-icon="attach_money"
                type="number" :min="0" step="0.01" color="secondary" outlined dense hide-details></v-text-field>
            </v-col>

            <v-col cols="12" sm="6" md="2">
              <v-select v-model.number="ivaPorcentajeGlobal" :items="ivaOptions"
                :label="$t('producto.modals.cambioMasivoPrecio.ivaGlobal')" prepend-icon="account_balance"
                color="secondary" outlined dense hide-details></v-select>
            </v-col>

            <v-col cols="12" sm="6" md="3">
              <v-text-field v-model.number="descuentoProveedorGlobal"
                :label="$t('producto.modals.cambioMasivoPrecio.descuentoGlobal')" prepend-icon="local_offer"
                type="number" :min="0" :max="100" suffix="%" color="secondary" outlined dense
                hide-details></v-text-field>
            </v-col>

            <v-col cols="12" sm="6" md="2">
              <v-text-field v-model.number="porcentajeSobreCostoGlobal"
                :label="$t('producto.modals.cambioMasivoPrecio.margenGlobal')" prepend-icon="trending_up" type="number"
                :min="0" suffix="%" color="secondary" outlined dense hide-details></v-text-field>
            </v-col>

            <v-col cols="12" sm="6" md="2">
              <v-select v-model="tipoRedondeoGlobal" :items="opcionesRedondeo"
                :label="$t('producto.modals.cambioMasivoPrecio.tipoRedondeo')" prepend-icon="mdi-currency-usd"
                color="secondary" outlined dense hide-details></v-select>
            </v-col>
          </v-row>

          <v-divider class="my-4"></v-divider>

          <!-- Tabla de Productos -->
          <v-row>
            <v-col cols="12">
              <h3 class="mb-3">{{ $t('producto.modals.cambioMasivoPrecio.productosSeleccionados') }}</h3>
              <v-alert v-if="productosConPrecio.length === 0" type="info" dense text class="mb-3">
                No hay productos seleccionados para actualizar.
              </v-alert>
              <div v-else style="max-height: 400px; overflow-y: auto;">
                <v-simple-table dense>
                  <template v-slot:default>
                    <thead>
                      <tr>
                        <th class="text-left">{{ $t('producto.labels.nombre') }}</th>
                        <th class="text-left">{{ $t('producto.labels.precioCompra') }}</th>
                        <th class="text-left">Descuento %</th>
                        <th class="text-left">IVA %</th>
                        <th class="text-left">Margen %</th>
                        <th class="text-left">{{ $t('producto.modals.cambioMasivoPrecio.precioVentaActual') }}</th>
                        <th class="text-left">{{ $t('producto.modals.cambioMasivoPrecio.diferencia') }}</th>
                        <th class="text-left" style="min-width: 140px;">{{ $t('producto.modals.cambioMasivoPrecio.precioVentaNuevo') }}</th>
                        <th class="text-center" style="width: 60px;">{{ $t('common.actions') }}</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="(producto, index) in productosConPrecio" :key="producto.id || index">
                        <td>
                          <div class="font-weight-medium">
                            {{ producto.nombre || '-' }}
                          </div>
                          <div class="grey--text text--darken-1 text-caption">
                            {{ (producto.marca && producto.marca.nombre) || '' }}
                            <span v-if="producto.modelo && producto.modelo.nombre">
                              - {{ producto.modelo.nombre }}
                            </span>
                            <span v-if="producto.tamanio && producto.tamanio.tamanio">
                              - {{ producto.tamanio.tamanio }}
                            </span>
                          </div>
                        </td>
                        <td>
                          <v-text-field v-model.number="producto.precioCompra" type="number" :min="0" step="0.01" dense
                            solo flat hide-details class="table-input" prefix="$"
                            @input="resetPrecioManual(producto)"></v-text-field>
                        </td>
                        <td>
                          <v-text-field v-model.number="producto.descuentoProveedor" type="number" :min="0" :max="100"
                            dense solo flat hide-details class="table-input-small" suffix="%"
                            @input="resetPrecioManual(producto)"></v-text-field>
                        </td>
                        <td>
                          <v-select v-model.number="producto.ivaPorcentaje" :items="ivaOptions" dense solo flat
                            hide-details class="table-input" @change="resetPrecioManual(producto)"></v-select>
                        </td>
                        <td>
                          <v-text-field v-model.number="producto.porcentajeSobreCosto" type="number" :min="0" dense solo
                            flat hide-details class="table-input-small" suffix="%"
                            @input="resetPrecioManual(producto)"></v-text-field>
                        </td>
                        <td>
                          <v-chip small>
                            ${{ formatCurrency(producto.precioVentaActual) }}
                          </v-chip>
                        </td>
                        <td>
                          <v-chip small :color="getDiferenciaColor(producto)" text-color="white">
                            <v-icon left x-small>
                              {{ getDiferenciaIcon(producto) }}
                            </v-icon>
                            {{ formatDiferencia(producto) }}
                          </v-chip>
                        </td>
                        <td class="col-precio-nuevo">
                          <v-text-field v-model.number="producto.precioVentaNuevo" type="number" :min="0" step="0.01"
                            dense solo flat hide-details class="table-input table-input-precio-nuevo" prefix="$"
                            :placeholder="formatCurrency(calcularPrecioAutomatico(producto))"></v-text-field>
                        </td>
                        <td class="text-center">
                          <v-btn icon x-small color="error" @click="removeProducto(index)" :disabled="loading">
                            <v-icon small>mdi-delete</v-icon>
                          </v-btn>
                        </td>
                      </tr>
                    </tbody>
                  </template>
                </v-simple-table>
              </div>
            </v-col>
          </v-row>
        </v-form>
      </v-card-text>
      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn text @click="close">
          {{ $t('common.cancel') }}
        </v-btn>
        <v-btn color="primary" @click="confirm" :disabled="!valid || productosConPrecio.length === 0 || loading"
          :loading="loading">
          {{ $t('common.confirm') }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import ProductoProvider from "../../../providers/ProductoProvider";

export default {
  name: "ProductoCambioMasivoModal",
  props: {
    open: { type: Boolean, default: false },
    selectedProductos: { type: Array, default: () => [] },
  },
  data() {
    return {
      valid: false,
      loading: false,
      productosConPrecio: [],
      // Valores globales
      precioCompraGlobal: null,
      ivaPorcentajeGlobal: null,
      descuentoProveedorGlobal: null,
      porcentajeSobreCostoGlobal: null,
      tipoRedondeoGlobal: 'ninguno',
      // Opciones de IVA
      ivaOptions: [
        { text: '0%', value: 0 },
        { text: '10.5%', value: 10.5 },
        { text: '21%', value: 21 },
        { text: '27%', value: 27 }
      ],
      // Opciones de redondeo
      opcionesRedondeo: [
        { text: 'Sin redondeo', value: 'ninguno' },
        { text: 'Redondear centavos (ej: 115.14 → 116.00)', value: 'centavos' },
        { text: 'Redondear a múltiplo de 10 (ej: 115.14 → 120.00)', value: 'multiplo_10' },
        { text: 'Redondear a múltiplo de 100 (ej: 115.14 → 200.00)', value: 'multiplo_100' }
      ],
    };
  },
  watch: {
    open(newVal) {
      if (newVal) {
        this.initialize();
      }
    },
    // Watchers para aplicar valores globales a todos los productos
    precioCompraGlobal(newVal) {
      if (newVal !== null && newVal !== undefined) {
        this.productosConPrecio.forEach(producto => {
          producto.precioCompra = newVal;
        });
      }
    },
    ivaPorcentajeGlobal(newVal) {
      if (newVal !== null && newVal !== undefined) {
        this.productosConPrecio.forEach(producto => {
          producto.ivaPorcentaje = newVal;
        });
      }
    },
    descuentoProveedorGlobal(newVal) {
      if (newVal !== null && newVal !== undefined) {
        this.productosConPrecio.forEach(producto => {
          producto.descuentoProveedor = newVal;
        });
      }
    },
    porcentajeSobreCostoGlobal(newVal) {
      if (newVal !== null && newVal !== undefined) {
        this.productosConPrecio.forEach(producto => {
          producto.porcentajeSobreCosto = newVal;
        });
      }
    },
    tipoRedondeoGlobal(newVal) {
      this.productosConPrecio.forEach(producto => {
        producto.tipoRedondeo = newVal;
      });
    },
  },
  mounted() {
    if (this.open) {
      this.initialize();
    }
  },
  methods: {
    initialize() {
      try {
        const selected = this.selectedProductos || [];

        this.productosConPrecio = selected
          .filter((producto) => producto.productoProveedorPrecioActivo && producto.productoProveedorPrecioActivo.id)
          .map((producto) => ({
            id: producto.id,
            nombre: producto.nombre,
            productoProveedorPrecioId: producto.productoProveedorPrecioActivo.id,
            marca: producto.marca,
            modelo: producto.modelo,
            tamanio: producto.tamanio,
            deposito: producto.deposito,
            precioCompra:
              producto.precioCompra ??
              (producto.productoProveedorPrecioActivo && producto.productoProveedorPrecioActivo.precioCompra) ??
              0,
            descuentoProveedor:
              (producto.productoProveedorPrecioActivo && producto.productoProveedorPrecioActivo.descuentoProveedor) ??
              producto.descuentoProveedor ??
              0,
            ivaPorcentaje: producto.ivaPorcentaje ?? 0,
            porcentajeSobreCosto: producto.porcentajeSobreCosto ?? 0,
            tipoRedondeo: 'ninguno',
            precioVentaActual: producto.precio || 0,
            precioVentaNuevo: null,
          }));

        if (selected.length > 0 && this.productosConPrecio.length < selected.length) {
          this.$emit(
            'info',
            `${selected.length - this.productosConPrecio.length} producto(s) sin proveedor activo no se incluyeron. Asigne un proveedor activo para actualizar su precio.`
          );
        }
      } catch (error) {
        console.error('Error building productos from selection:', error);
        this.productosConPrecio = [];
      }
    },
    calcularPrecioAutomatico(producto) {
      if (!producto.precioCompra || producto.precioCompra <= 0) {
        return 0;
      }

      // 1. Aplicar descuento del proveedor
      let precio = producto.precioCompra * (1 - (producto.descuentoProveedor || 0) / 100);

      // 2. Aplicar IVA
      precio = precio * (1 + (producto.ivaPorcentaje || 0) / 100);

      // 3. Aplicar margen sobre costo
      precio = precio * (1 + (producto.porcentajeSobreCosto || 0) / 100);

      // 4. Aplicar redondeo según el tipo seleccionado
      const tipoRedondeo = producto.tipoRedondeo || 'ninguno';

      if (precio > 0 && tipoRedondeo !== 'ninguno') {
        switch (tipoRedondeo) {
          case 'centavos':
            // Redondear al entero superior (ej: 115.14 → 116.00)
            precio = Math.ceil(precio);
            break;
          case 'multiplo_10':
            // Redondear al múltiplo de 10 superior (ej: 115.14 → 120.00)
            precio = Math.ceil(precio / 10) * 10;
            break;
          case 'multiplo_100':
            // Redondear al múltiplo de 100 superior (ej: 115.14 → 200.00)
            precio = Math.ceil(precio / 100) * 100;
            break;
        }
      }

      return parseFloat(precio.toFixed(2));
    },
    calcularPrecioVentaNuevo(producto) {
      // Si hay un precio manual, usarlo; sino, calcular automáticamente
      if (producto.precioVentaNuevo !== null && producto.precioVentaNuevo !== undefined && producto.precioVentaNuevo !== '') {
        return parseFloat(producto.precioVentaNuevo);
      }
      return this.calcularPrecioAutomatico(producto);
    },
    getPrecioColor(producto) {
      const precioNuevo = this.calcularPrecioVentaNuevo(producto);
      const precioActual = producto.precioVentaActual || 0;

      if (precioNuevo > precioActual) {
        return 'green';
      } else if (precioNuevo < precioActual) {
        return 'red';
      }
      return 'grey';
    },
    getDiferenciaColor(producto) {
      const precioNuevo = this.calcularPrecioVentaNuevo(producto);
      const precioActual = producto.precioVentaActual || 0;
      const diferencia = ((precioNuevo - precioActual) / precioActual) * 100;

      if (Math.abs(diferencia) > 1) {
        return diferencia > 0 ? 'success' : 'error';
      }
      return 'grey';
    },
    getDiferenciaIcon(producto) {
      const precioNuevo = this.calcularPrecioVentaNuevo(producto);
      const precioActual = producto.precioVentaActual || 0;

      if (precioNuevo > precioActual) {
        return 'mdi-arrow-up';
      } else if (precioNuevo < precioActual) {
        return 'mdi-arrow-down';
      }
      return 'mdi-minus';
    },
    formatDiferencia(producto) {
      const precioNuevo = this.calcularPrecioVentaNuevo(producto);
      const precioActual = producto.precioVentaActual || 0;

      if (precioActual === 0) {
        return 'N/A';
      }

      const diferenciaMonto = precioNuevo - precioActual;
      const diferenciaPorcentaje = ((precioNuevo - precioActual) / precioActual) * 100;

      const signo = diferenciaMonto >= 0 ? '+' : '';
      return `${signo}$${diferenciaMonto.toFixed(2)} (${signo}${diferenciaPorcentaje.toFixed(1)}%)`;
    },
    formatCurrency(value) {
      return parseFloat(value || 0).toFixed(2);
    },
    resetPrecioManual(producto) {
      // Resetear el precio manual a null para que se recalcule automáticamente
      producto.precioVentaNuevo = null;
    },
    removeProducto(index) {
      this.productosConPrecio.splice(index, 1);
      if (this.productosConPrecio.length === 0) {
        this.$emit('info', 'Todos los productos han sido eliminados de la selección');
        this.close();
      }
    },
    async confirm() {
      if (!this.$refs.form.validate()) {
        return;
      }

      // Validar que todos los productos tengan precio de compra
      const productosInvalidos = this.productosConPrecio.filter(p => !p.precioCompra || p.precioCompra <= 0);
      if (productosInvalidos.length > 0) {
        this.$emit('error', 'Todos los productos deben tener un precio de compra válido');
        return;
      }

      this.loading = true;
      try {
        const productos = this.productosConPrecio.map(producto => {
          const precioFinal = this.calcularPrecioVentaNuevo(producto);
          return {
            id: producto.id,
            productoProveedorPrecioId: producto.productoProveedorPrecioId,
            precioCompra: producto.precioCompra,
            descuentoProveedor: producto.descuentoProveedor || 0,
            ivaPorcentaje: producto.ivaPorcentaje || 0,
            porcentajeSobreCosto: producto.porcentajeSobreCosto || 0,
            tipoRedondeo: producto.tipoRedondeo || 'ninguno',
            precioVentaManual: parseFloat(precioFinal),
          };
        });

        const response = await ProductoProvider.updateManyPricing(productos);

        if (response && response.data && response.data.productoUpdateManyPricing) {
          const result = response.data.productoUpdateManyPricing;
          if (result.ok) {
            this.$emit('success', result.mensaje || this.$t('producto.modals.cambioMasivoPrecio.success'));
            this.close();
          } else {
            this.$emit('error', result.mensaje || this.$t('producto.modals.cambioMasivoPrecio.error'));
          }
        } else {
          this.$emit('error', this.$t('producto.modals.cambioMasivoPrecio.error'));
        }
      } catch (error) {
        const errorMessage = (error && error.graphQLErrors && error.graphQLErrors[0] && error.graphQLErrors[0].message) ||
          (error && error.message) ||
          this.$t('producto.modals.cambioMasivoPrecio.error');
        this.$emit('error', errorMessage);
      } finally {
        this.loading = false;
      }
    },
    close() {
      this.reset();
      this.$emit('close');
    },
    reset() {
      this.productosConPrecio = [];
      this.precioCompraGlobal = null;
      this.ivaPorcentajeGlobal = null;
      this.descuentoProveedorGlobal = null;
      this.porcentajeSobreCostoGlobal = null;
      this.valid = false;
      if (this.$refs.form) {
        this.$refs.form.resetValidation();
      }
    },
  },
};
</script>

<style scoped>
/* Fijar altura de las filas de la tabla */
::v-deep .v-data-table tbody tr {
  height: 60px !important;
}

::v-deep .v-data-table tbody td {
  height: 60px !important;
  padding-top: 8px !important;
  padding-bottom: 8px !important;
}

/* Estilos para inputs de la tabla */
::v-deep .table-input {
  max-width: 100px;
}

/* Columna precio nuevo de venta: un poco más ancha */
::v-deep .table-input-precio-nuevo {
  max-width: 140px;
}

::v-deep .table-input .v-input__control {
  min-height: 40px !important;
}

::v-deep .table-input .v-input__slot {
  background-color: #ffffff !important;
  border: 2px solid #7986cb !important;
  border-radius: 6px !important;
  padding: 0 10px !important;
  transition: all 0.2s ease;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
}

::v-deep .table-input .v-input__slot:hover {
  border-color: #5c6bc0 !important;
  box-shadow: 0 2px 6px rgba(63, 81, 181, 0.15);
}

::v-deep .table-input .v-text-field__prefix,
::v-deep .table-input .v-text-field__suffix {
  color: #1a237e;
  font-weight: 600;
  font-size: 14px;
}

::v-deep .table-input-small {
  max-width: 80px;
}

::v-deep .table-input-small .v-input__control {
  min-height: 40px !important;
}

::v-deep .table-input-small .v-input__slot {
  background-color: #ffffff !important;
  border: 2px solid #7986cb !important;
  border-radius: 6px !important;
  padding: 0 10px !important;
  transition: all 0.2s ease;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
}

::v-deep .table-input-small .v-input__slot:hover {
  border-color: #5c6bc0 !important;
  box-shadow: 0 2px 6px rgba(63, 81, 181, 0.15);
}

::v-deep .table-input-small .v-text-field__suffix {
  color: #1a237e;
  font-weight: 600;
  font-size: 14px;
}

/* Input focus effect - más prominente */
::v-deep .table-input.v-input--is-focused .v-input__slot,
::v-deep .table-input-small.v-input--is-focused .v-input__slot {
  background-color: #e8eaf6 !important;
  border-color: #3f51b5 !important;
  box-shadow: 0 0 0 3px rgba(63, 81, 181, 0.2), 0 2px 8px rgba(63, 81, 181, 0.25);
}

/* Input number styling */
::v-deep .table-input input[type="number"],
::v-deep .table-input-small input[type="number"] {
  text-align: center;
  font-weight: 600;
  font-size: 14px;
  color: #1a237e;
}

/* Placeholder styling - mismo estilo que el input para que se vea idéntico */
::v-deep .table-input input[type="number"]::placeholder,
::v-deep .table-input-small input[type="number"]::placeholder {
  color: #1a237e;
  font-weight: 600;
  opacity: 1;
}

/* Remove spinner from number inputs */
::v-deep .table-input input[type="number"]::-webkit-inner-spin-button,
::v-deep .table-input input[type="number"]::-webkit-outer-spin-button,
::v-deep .table-input-small input[type="number"]::-webkit-inner-spin-button,
::v-deep .table-input-small input[type="number"]::-webkit-outer-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

::v-deep .table-input input[type="number"],
::v-deep .table-input-small input[type="number"] {
  -moz-appearance: textfield;
}
</style>
